import React, { useMemo } from 'react';
import { Row, Col } from 'react-bootstrap';
import { HEAD_DOCTOR_COPY } from '../headDoctorCopy';
import { useClinicalReports } from '../../clinical-reports/ClinicalReportsContext';
import { SERVICE_LINE_LABELS, formatMonthKey } from './reportUtils';
import ReportSectionHead from './ReportSectionHead';
import StatisticalDataTable, {
  statNumberColumn,
  statPercentColumn,
  statRankColumn
} from './StatisticalDataTable';
import { HeadDoctorStatCard } from '../ui';
import {
  ClinicalBoxPlotChart,
  ClinicalDoughnutChart,
  ClinicalLineChart,
  ReportChartCard,
  ReportsChartCol,
  ReportsChartGrid
} from './charts/ClinicalCharts';
import { CHART_PALETTE, SERVICE_LINE_COLORS } from './charts/chartTheme';
import {
  computeShares,
  doughnutChartData,
  labeledPieData,
  lineChartData,
  pathologyBoxplotChartData,
  topNWithOthers
} from './charts/chartDataHelpers';

const COPY = HEAD_DOCTOR_COPY.REPORTS;

export default function HeadDoctorReportsPathologiesPage() {
  const { data, summary, loading } = useClinicalReports();
  const rows = useMemo(
    () => computeShares(data?.pathologies?.rows || [], 'consultation_count'),
    [data]
  );
  const byMonth = data?.pathologies?.by_month || [];
  const byLine = data?.pathologies?.by_service_line || [];
  const boxplotSeries = data?.pathologies?.boxplot_series || [];

  const uniquePathologies = Number(summary.unique_pathologies || 0);
  const occurrences = Number(summary.pathology_occurrences || 0);
  const consultationsCoded = Number(summary.consultations_with_pathology || 0);
  const totalConsultations = Number(summary.consultations || 0);
  const codingRate =
    totalConsultations > 0 ? Math.round((consultationsCoded / totalConsultations) * 100) : 0;

  const boxplotData = useMemo(
    () => pathologyBoxplotChartData(boxplotSeries, rows, COPY.CHART_PATHO_BOXPLOT_SERIES),
    [boxplotSeries, rows]
  );

  const pieData = useMemo(() => {
    const pie = topNWithOthers(rows, 'consultation_count', 'disease_name', 5, COPY.CHART_OTHERS);
    return doughnutChartData(pie.labels, pie.values, pie.colors);
  }, [rows]);

  const linePie = useMemo(
    () =>
      labeledPieData(byLine, {
        labelKey: 'service_line',
        valueKey: 'consultation_count',
        labelMap: SERVICE_LINE_LABELS,
        colorMap: SERVICE_LINE_COLORS
      }),
    [byLine]
  );

  const trendData = useMemo(
    () =>
      lineChartData(
        byMonth.map((row) => formatMonthKey(row.month_key)),
        [
          {
            label: COPY.CHART_PATHO_TREND_CONSULTATIONS,
            data: byMonth.map((row) => Number(row.consultation_count)),
            color: CHART_PALETTE[0],
            fill: true
          },
          {
            label: COPY.CHART_PATHO_TREND_OCCURRENCES,
            data: byMonth.map((row) => Number(row.occurrence_count)),
            color: CHART_PALETTE[1],
            fill: false
          }
        ]
      ),
    [byMonth]
  );

  const tableColumns = [
    {
      key: 'rank',
      label: '#',
      className: 'text-muted',
      render: (_row, idx) => idx + 1
    },
    {
      key: 'disease_name',
      label: COPY.COL_PATHOLOGY,
      className: 'fw-semibold',
      render: (row) => row.disease_name
    },
    {
      key: 'disease_code',
      label: COPY.COL_CODE,
      render: (row) => <code className="hd-reports-code">{row.disease_code || '—'}</code>
    },
    statRankColumn(COPY.COL_CONSULTATIONS, 'consultation_count'),
    statPercentColumn(COPY.COL_SHARE),
    statNumberColumn(COPY.COL_PATIENTS, 'patient_count')
  ];

  const empty = !loading && uniquePathologies === 0 && rows.length === 0;
  const hasCharts =
    !loading &&
    !empty &&
    (rows.length > 0 || byMonth.length > 0 || byLine.length > 0 || boxplotData.labels.length > 0);
  const boxplotHeight = Math.max(280, (boxplotData.labels.length || 1) * 42 + 80);

  return (
    <>
      <ReportSectionHead
        badge={COPY.SUB_PATHOLOGIES}
        title={COPY.PAGE_PATHOLOGIES_TITLE}
        description={COPY.PAGE_PATHOLOGIES_DESC}
      />

      <Row className="g-3 mb-4">
        <Col sm={6} lg={4}>
          <HeadDoctorStatCard
            title={COPY.KPI_PATHOLOGIES}
            value={uniquePathologies}
            icon="alertCircle"
            tone="amber"
            loading={loading}
          />
        </Col>
        <Col sm={6} lg={4}>
          <HeadDoctorStatCard
            title={COPY.KPI_PATHO_OCCURRENCES}
            value={occurrences}
            icon="list"
            tone="indigo"
            loading={loading}
          />
        </Col>
        <Col sm={6} lg={4}>
          <HeadDoctorStatCard
            title={COPY.KPI_PATHO_CONSULTATIONS}
            value={consultationsCoded}
            icon="stethoscope"
            tone="teal"
            loading={loading}
            subtitle={
              totalConsultations > 0
                ? `${codingRate} % ${COPY.KPI_PATHOLOGY_RATE_HINT}`
                : undefined
            }
          />
        </Col>
      </Row>

      {loading ? (
        <div className="hd-reports-panel mb-4">
          <div className="hd-reports-loading">
            <span className="spinner-border spinner-border-sm" role="status" aria-hidden />
            <span>{COPY.LOADING}</span>
          </div>
        </div>
      ) : hasCharts ? (
        <>
          <ReportsChartGrid>
            <ReportsChartCol lg={7}>
              <ReportChartCard
                title={COPY.CHART_PATHO_BAR_TITLE}
                subtitle={COPY.CHART_PATHO_BAR_DESC}
                height={boxplotHeight}
              >
                {boxplotData.labels.length > 0 ? (
                  <ClinicalBoxPlotChart
                    data={boxplotData}
                    horizontal
                    xLabel={COPY.CHART_AXIS_CONSULTATIONS}
                  />
                ) : (
                  <p className="hd-reports-empty mb-0">{COPY.EMPTY_PATHOLOGIES}</p>
                )}
              </ReportChartCard>
            </ReportsChartCol>
            <ReportsChartCol lg={5}>
              <ReportChartCard
                title={COPY.CHART_PATHO_PIE_TITLE}
                subtitle={COPY.CHART_PATHO_PIE_DESC}
                height={boxplotHeight}
              >
                <ClinicalDoughnutChart data={pieData} />
              </ReportChartCard>
            </ReportsChartCol>
          </ReportsChartGrid>

          {(byMonth.length > 0 || byLine.length > 0) && (
            <ReportsChartGrid>
              {byMonth.length > 0 ? (
                <ReportsChartCol lg={byLine.length > 0 ? 8 : 12}>
                  <ReportChartCard
                    title={COPY.CHART_PATHO_TREND_TITLE}
                    subtitle={COPY.CHART_PATHO_TREND_DESC}
                    height={280}
                  >
                    <ClinicalLineChart
                      data={trendData}
                      xLabel={COPY.CHART_AXIS_PERIOD}
                      yLabel={COPY.CHART_AXIS_CONSULTATIONS}
                    />
                  </ReportChartCard>
                </ReportsChartCol>
              ) : null}
              {byLine.length > 0 ? (
                <ReportsChartCol lg={byMonth.length > 0 ? 4 : 12}>
                  <ReportChartCard
                    title={COPY.CHART_PATHO_LINE_TITLE}
                    subtitle={COPY.CHART_PATHO_LINE_DESC}
                    height={280}
                  >
                    <ClinicalDoughnutChart
                      data={doughnutChartData(linePie.labels, linePie.values, linePie.colors)}
                    />
                  </ReportChartCard>
                </ReportsChartCol>
              ) : null}
            </ReportsChartGrid>
          )}
        </>
      ) : (
        <div className="hd-reports-panel mb-4">
          <p className="hd-reports-empty mb-0">{COPY.EMPTY_PATHOLOGIES}</p>
        </div>
      )}

      {rows.length > 0 || loading ? (
        <>
          <h3 className="hd-reports-table-title">{COPY.TABLE_PATHOLOGIES_TITLE}</h3>
          <StatisticalDataTable
            columns={tableColumns}
            rows={rows}
            rowKey="disease_id"
            maxValueKey="consultation_count"
            loading={loading}
            emptyLabel={COPY.EMPTY_PATHOLOGIES}
            loadingLabel={COPY.LOADING}
          />
        </>
      ) : null}
    </>
  );
}
